import React, { useState } from 'react';
import { 
  Package, 
  User, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Barcode,
  Plus,
  Eye,
  Edit
} from 'lucide-react';

const KitAssembly = () => {
  const [selectedBatch, setSelectedBatch] = useState(null);

  const activeBatches = [
    {
      batchNumber: 'NAL-2024-001',
      kitType: 'Take-Home Naloxone Kit',
      kitSku: 'NAL-HOME-01',
      targetQuantity: 500,
      completedQuantity: 425,
      status: 'In Progress',
      assignedWorker: 'Maria Rodriguez',
      workerId: 'W001',
      startDate: '2024-01-15 08:00',
      estimatedCompletion: '2024-01-17 16:00',
      baseKitLot: 'NAL-BASE-240115-A',
      baseKitExpiry: '2026-01-15',
      components: [
        { sku: 'NAL-001', name: 'Naloxone Nasal Spray 4mg', lot: 'LOT-2024-001', expiry: '2025-12-31', required: 2, used: 850 },
        { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', lot: 'LOT-2024-002', expiry: '2025-11-30', required: 1, used: 425 },
        { sku: 'FACE-003', name: 'Face Shield/Barrier', lot: 'LOT-2024-003', expiry: '2027-10-15', required: 1, used: 425 },
        { sku: 'GLOV-004', name: 'Nitrile Gloves (Pairs)', lot: 'LOT-2024-004', expiry: '2025-09-30', required: 2, used: 850 }
      ]
    },
    {
      batchNumber: 'NAL-2024-002',
      kitType: 'Training Naloxone Kit',
      kitSku: 'NAL-TRAIN-01',
      targetQuantity: 200,
      completedQuantity: 120,
      status: 'In Progress',
      assignedWorker: 'James Chen',
      workerId: 'W002',
      startDate: '2024-01-16 09:00',
      estimatedCompletion: '2024-01-18 17:00',
      baseKitLot: 'NAL-BASE-240116-B',
      baseKitExpiry: '2026-01-16',
      components: [
        { sku: 'NAL-002', name: 'Training Naloxone (Inert)', lot: 'LOT-2024-005', expiry: '2026-12-31', required: 2, used: 240 },
        { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', lot: 'LOT-2024-002', expiry: '2025-11-30', required: 1, used: 120 },
        { sku: 'TRAIN-006', name: 'Training Manual', lot: 'LOT-2024-006', expiry: '2026-09-30', required: 1, used: 120 }
      ]
    },
    {
      batchNumber: 'NAL-2024-003',
      kitType: 'Professional Naloxone Kit',
      kitSku: 'NAL-PROF-01',
      targetQuantity: 100,
      completedQuantity: 100,
      status: 'Completed',
      assignedWorker: 'Sarah Johnson',
      workerId: 'W003',
      completedBy: 'Sarah Johnson',
      completedDate: '2024-01-15 15:30',
      startDate: '2024-01-14 08:00',
      baseKitLot: 'NAL-BASE-240114-C',
      baseKitExpiry: '2026-01-14',
      components: [
        { sku: 'NAL-001', name: 'Naloxone Nasal Spray 4mg', lot: 'LOT-2024-001', expiry: '2025-12-31', required: 4, used: 400 },
        { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', lot: 'LOT-2024-002', expiry: '2025-11-30', required: 1, used: 100 },
        { sku: 'FACE-003', name: 'Face Shield/Barrier', lot: 'LOT-2024-003', expiry: '2027-10-15', required: 2, used: 200 },
        { sku: 'GLOV-004', name: 'Nitrile Gloves (Pairs)', lot: 'LOT-2024-004', expiry: '2025-09-30', required: 4, used: 400 },
        { sku: 'STET-007', name: 'Stethoscope', lot: 'LOT-2024-007', expiry: '2027-01-01', required: 1, used: 100 }
      ]
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-yellow-100 text-yellow-800',
      'Quality Review': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getProgressPercentage = (completed, target) => {
    return Math.round((completed / target) * 100);
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Naloxone Kit Assembly & Manufacturing</h1>
        <p className="text-gray-600">Track Naloxone kit assembly batches and component traceability</p>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          <div className="flex space-x-4">
            <button 
              onClick={() => alert('Start New Batch\n\nThis feature will be available once the backend is implemented.\n\nComing soon:\n• Create new production batch\n• Assign workers\n• Set target quantities\n• Assign component lots')}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Start New Batch</span>
            </button>
            <button 
              onClick={() => alert('Complete Batch\n\nThis feature will be available once the backend is implemented.\n\nComing soon:\n• Mark batch as complete\n• Generate traceability report\n• Update inventory\n• Send to Quality Control')}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Complete Batch</span>
            </button>
          </div>
        </div>
      </div>

      {/* Active Batches */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {activeBatches.map((batch) => (
          <div key={batch.batchNumber} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{batch.batchNumber}</h3>
                  <p className="text-sm text-gray-600">{batch.kitType}</p>
                  <p className="text-xs text-gray-500">SKU: {batch.kitSku}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                  {batch.status}
                </span>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-gray-900">
                    {batch.completedQuantity}/{batch.targetQuantity} kits ({getProgressPercentage(batch.completedQuantity, batch.targetQuantity)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-red-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage(batch.completedQuantity, batch.targetQuantity)}%` }}
                  ></div>
                </div>
              </div>

              {/* Worker Info */}
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Worker:</span>
                  <span className="font-medium text-gray-900">{batch.assignedWorker}</span>
                  <span className="text-gray-500">({batch.workerId})</span>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-6">
              {/* Base Naloxone Kit Info */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <Package className="h-4 w-4 mr-2 text-blue-600" />
                  Base Naloxone Kit
                </h4>
                <div className="text-sm space-y-1">
                  <p><span className="text-gray-600">Lot Number:</span> <span className="font-medium">{batch.baseKitLot}</span></p>
                  <p><span className="text-gray-600">Expiry:</span> <span className="font-medium">{batch.baseKitExpiry}</span></p>
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Timeline</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-green-500" />
                    <span className="text-gray-600">Started:</span>
                    <span className="font-medium text-gray-900">{batch.startDate}</span>
                  </div>
                  {batch.status === 'Completed' ? (
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-gray-600">Completed:</span>
                      <span className="font-medium text-gray-900">{batch.completedDate}</span>
                      <span className="text-gray-600">by</span>
                      <span className="font-medium text-gray-900">{batch.completedBy}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className="text-gray-600">Est. Completion:</span>
                      <span className="font-medium text-gray-900">{batch.estimatedCompletion}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Components Used */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Component Traceability</h4>
                <div className="space-y-3">
                  {batch.components.map((component, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{component.name}</span>
                        <span className="text-sm text-gray-600">SKU: {component.sku}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Lot:</span> 
                          <span className="font-medium text-gray-900 ml-2">{component.lot}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Expiry:</span> 
                          <span className="font-medium text-gray-900 ml-2">{component.expiry}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Per Kit:</span> 
                          <span className="font-medium text-gray-900 ml-2">{component.required}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Used:</span> 
                          <span className="font-medium text-gray-900 ml-2">{component.used.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => alert(`Batch Details: ${batch.batchNumber}\n\nKit Type: ${batch.kitType}\nStatus: ${batch.status}\nWorker: ${batch.assignedWorker}\nProgress: ${batch.completedQuantity}/${batch.targetQuantity}\n\nFull details will be available after backend implementation.`)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  <span>View Details</span>
                </button>
                {batch.status !== 'Completed' && (
                  <button 
                    onClick={() => alert(`Update Progress: ${batch.batchNumber}\n\nThis feature will allow you to:\n• Update completed quantity\n• Add quality notes\n• Record component usage\n• Scan barcodes\n\nAvailable after backend implementation.`)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Update Progress</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KitAssembly;